import { fg } from '@atlaskit/platform-feature-flags';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

export type WebSearchConfigurationResponse = {
	resourceAri: string;
	enabled: boolean;
};

export type WebSearchConfiguration = {
	webSearchEnabled: boolean;
	isLoading: boolean;
	error?: string;
};

export const useWebSearchConfiguration: (
	props: Omit<AssistanceServiceInjectedParams, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => {
	webSearchEnabled: boolean;
	isLoading: boolean;
	error: string | undefined;
} = createHookWithAssistanceService(
	({ assistanceServiceInjectedParams: { assistanceService } }: AssistanceServiceInjectedParams) => {
		// Check feature flag first - if disabled, don't make the API request
		const isFeatureFlagEnabled = fg('rovo_chat_web_search_enabled');

		const { data, error, isFetching } = useQuery({
			queryKey: ['webSearchConfiguration'],
			queryFn: async (): Promise<WebSearchConfigurationResponse> => {
				return await assistanceService.getWebSearchConfiguration();
			},
			// Only fetch if the feature flag is enabled
			enabled: isFeatureFlagEnabled,
		});

		return {
			webSearchEnabled: isFeatureFlagEnabled && (data?.enabled ?? false),
			isLoading: isFetching,
			error:
				error instanceof Error
					? error.message
					: error
						? 'Failed to fetch web search configuration'
						: undefined,
		};
	},
);
